import {useQuery} from "@tanstack/react-query";
import {useClientV2} from "../../utils/hooks";
import {AxiosError} from "axios";
import {Currency} from "../../components/calls/service/service-form/i-service-form";
import {TProfileApiResponse} from "../../redux/profile/i-profile-1";
import {CurrencyResponse, PaginationMeta} from "../../redux/service/i-service";
import {buildPaginationMeta} from "../customers/subscriptions/shared/pagination-meta";
import {BaseProductApiResponse, CoverImagesItemResponse} from "./types";
import {createDigitalProductRepository} from "../../features/digital-products";
import {createMediaRepository} from "../../features/media";

export const product_module_query_key = "digital_products";
export const single_product_query_key = "digital_product";
const module_customer_query_key = "digital_products_customer";

export type TGetProductRes = {
  id: number;
  userId: number;
  title: string;
  slug: string;
  description: string;
  coverImage: string;
  tiny_url: string;
  currency: CurrencyResponse;
  hideFromProfile: boolean;
  is_deleted: boolean;
  request_phone_number: boolean;
  url_redirect?: string;
  statusId: number;
  reference: string;
  createdAt: string;
  updatedAt: string;
  enable_currency_conversion: boolean;
  selected_currencies: string[];
};
export type TProductMedia = {
  id: number;
  userId: number;
  productId: number;
  file_name: string;
  file_type: string;
  file_size: string;
  media_url: string;
  createdAt: string;
  updatedAt: string;
};

export type TGetProductsApiResponse = {
  products: {
    coverImage: string;
    cover_images: BaseProductApiResponse["cover_images"];
    createdAt: string;
    defaultPrice: {amount: number; currency: string} | null;
    description: string;
    hideFromProfile: boolean;
    id: number;
    is_deleted: boolean;
    noOfSales: number;
    productCurrencies: [];
    reference: string;
    request_phone_number: boolean;
    revenue: number;
    slug: string;
    statusId: string;
    tiny_url: string;
    title: string;
    updatedAt: string;
    userId: number;
  }[];
};

/**
 * A row in the customer's purchased digital-product library.
 *
 * Sourced from v2 `GET customer/digital-products`, whose DTO
 * (`CustomerDigitalProductPurchaseResponseDto`) exposes 10 fields, so every
 * field the v2 payload cannot supply is optional. The required fields are
 * exactly the ones the list/card render.
 */
export type GetCustomerProductsSingleProductResponse = {
  amount: number;
  coverImage: string;
  cover_images?: CoverImagesItemResponse[];
  createdAt: string;
  currency: string;
  description: string;
  /** Numeric product id (v2 `productId`) — the React list key and review-lookup key. */
  id: number;
  reference: string;
  title: string;
  /** v2 `urlRedirect` — drives the drawer's "your purchase includes a link" block. */
  url_redirect?: string;
  hideFromProfile?: boolean;
  is_deleted?: boolean;
  request_phone_number?: boolean;
  slug?: string;
  statusId?: number;
  tiny_url?: string;
  updatedAt?: string;
  userId?: number;
  enable_currency_conversion?: boolean;
  selected_currencies?: string[];
};

export type TGetCustomerProductsApiResponse = {
  products: GetCustomerProductsSingleProductResponse[];
};

export type TGetProductApiResponse = {
  product: TGetProductRes;
  totalSales: number;
  medias: TProductMedia[];
  currencies: CurrencyResponse[];
  revenue: {amount: number; currency: string; units: number}[];
  enable_currency_conversion: boolean;
  selected_currencies: string[];
};
/**
 * One asset file the customer received for a purchase.
 *
 * Mirrors v2 `CustomerDigitalProductAssetResponseDto`. `id` is the media-bucket
 * row id and is the React key for the asset lists.
 */
export type CustomerPurchaseAsset = {
  id: number;
  fileName: string;
  fileType: string;
  /** Human-readable size string (e.g. "1.9 MB"), not a byte count. */
  fileSize: string;
  mediaUrl: string;
};

/** One priced option inside a purchased add-on. */
export type CustomerPurchaseAddOnOption = {
  id: string;
  optionName: string;
  price: number;
  currency: string;
};

/** One purchased add-on (variant) with the option(s) the customer selected. */
export type CustomerPurchaseAddOn = {
  id: string;
  variantName: string;
  options: CustomerPurchaseAddOnOption[];
};

/**
 * Full customer-facing detail for one purchase, from v2
 * `GET customer/digital-products/:reference`
 * (`CustomerDigitalProductDetailResponseDto`).
 *
 * Keyed by the purchase `reference`; v1's equivalent was keyed by numeric
 * product id and returned a much wider payload (full coach record, raw
 * transaction row, currency list). Only the fields the customer UI actually
 * renders survive here.
 *
 * `amount` is the amount the purchase was charged at and **already includes the
 * add-on option prices** (v2 folds them into the transaction amount before
 * charging, then applies any discount to the sum). Do not add the add-on prices
 * on top of it.
 */
export type TGetCustomerProductDetailApiResponse = {
  reference: string;
  currency: string;
  amount: number;
  createdAt: string;
  product: {
    id: number;
    name: string;
    slug: string;
    tinyUrl: string;
    description: string;
    /** `null` when the product ships no redirect link. */
    urlRedirect: string | null;
    coverImage: string;
  };
  coach: {
    username: string;
    coachDisplayName: string;
    shortBio: string;
    profilePhoto: string | null;
  };
  assets: CustomerPurchaseAsset[];
  /**
   * Genuinely purchased add-ons. Empty for v2-era purchases made before the
   * backend started persisting them, so consumers must render nothing rather
   * than an empty section.
   */
  addOns: CustomerPurchaseAddOn[];
};

export type TCreateProductPayload = {
  title: string;
  description: string;
  url_redirect?: string;
  coverImage: string;
  currencies: Currency[];
  hideProductFromProfile: boolean;
  request_phone_number: boolean;
  enable_currency_conversion?: boolean;
  selected_currencies?: string[];
};

export type TCreateProductApiResponse = {
  product: {
    userId: number;
    title: string;
    slug: string;
    description: string;
    coverImage: string;
    hideFromProfile: boolean;
    statusId: string;
    reference: string;
    id: number;
    is_deleted: boolean;
    createdAt: string;
    updatedAt: string;
  };
};

export type TUpdateProductPayload = {
  title: string;
  description: string;
  coverImage: string;
  currencies: Currency[];
  hideProductFromProfile: boolean;
  request_phone_number: boolean;
  productId: number;
  enable_currency_conversion?: boolean;
  selected_currencies?: string[];
};

export type TPublicProfileProductDetailsApiResponse = {
  product: TGetProductRes;
  profile: TProfileApiResponse;
  user: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    onboarding_reminder: number;
    country: string;
    password: null;
    statusId: number;
    isSocialAuth: boolean;
    avatar: string;
    cover_image: string;
    createdAt: string;
    updatedAt: string;
    display_name: string | null;
  };
  currencies: CurrencyResponse[];
  medias: TProductMedia[];
};

export type TPurchaseProductPayload = {
  firstName: string;
  lastName: string;
  emailAddress: string;
  productId: number;
  phone_number?: string;
  currencyId: number | null;
};

export type TPurchaseProductApiResponse = {
  amount: number;
  currency: string;
  isPaid: boolean;
  isStripe: boolean;
  firstName: string;
  lastName: string;
  emailAddress: string;
  redirectUrl?: string;
  reference: string;
  paymentChannel: string;
};

export type TRenameContentPayload = {
  fileName: string;
  fileExtension: string;
  productId: number;
  contentId: number;
};
export type TUploadContentPayload = {
  fileName: string;
  fileType: string;
  fileExtension: string;
  productId: number;
  data: Blob;
};

export type TGetProductCustomersApiResponse = {
  customers: {
    amount: number;
    bookingQuestions: {answer?: string | null; question: string}[];
    createdAt: string;
    currency: string;
    emailAddress: string;
    firstName: string;
    id: number;
    lastName: string;
    phoneNumber: string | null;
    reference: string;
    variants: {
      currency: string;
      optionName: string;
      price: number;
      variantName: string;
    }[];
  }[];
};

/**
 * Builds the all-time analytics window the preview page's revenue/sales tiles
 * need: from the product's creation date to today. Mirrors the private
 * `getAllTimeRange` helper in `features/digital-products/hooks`.
 */
const productAllTimeRange = (createdAt?: string) => ({
  startDate: createdAt ? createdAt.slice(0, 10) : "2020-01-01",
  endDate: new Date().toISOString().slice(0, 10),
});

/**
 * Single digital product for the standalone `/products/view/:id` preview route
 * (`view-product-context.tsx`) — now v2, composed from `features/digital-products`
 * and `features/media` instead of the decommissioned v1 `GET product/:id`.
 *
 * v1 answered one fat payload; v2 splits it across four reads, all of which the
 * feature modules already own (each unwrapping its own
 * `{status, message, statusCode, data}` envelope in its mapper):
 *
 * - `GET digital-products/single/:productId` -> product core. Note the v2 DTO has
 *   no `coverImage` and the feature mapper drops `currencies`, so the entity's
 *   `currencies` getter falls back to the single base `callPrice` entry — the
 *   same reduced-but-correct pricing list the already-migrated edit flow shows.
 * - `GET digital-products/:productId/content` -> `medias`.
 * - `GET analytics/digital-products/sales-insights/:productId` -> `totalSales`
 *   and `revenue` (v2 returns `totalRevenueByCurrency` as a map).
 * - `GET uploads/cover-image?offeringType=product&offeringId=:id` -> the banner
 *   image, which v2 keeps in the media module rather than on the product.
 *
 * Only the product read is load-bearing: the three enrichment reads degrade to
 * empty/zero rather than failing the whole page, which is how the preview
 * behaved when v1 returned a product with no content or sales.
 *
 * Hook signature, query key, `enabled` gating and the `TGetProductApiResponse`
 * return shape are unchanged, so the context and both detail components need no
 * changes.
 */
export const useGetProduct = (productId?: string) => {
  const digitalProductRepository = createDigitalProductRepository();
  const mediaRepository = createMediaRepository();

  return useQuery<TGetProductApiResponse, AxiosError<{status: boolean; message: string}>>(
    {
      queryKey: [single_product_query_key, String(productId)],
      queryFn: async () => {
        const id = Number(productId);
        const product = await digitalProductRepository.getSingle(id);

        const [contents, insights, coverImages] = await Promise.all([
          digitalProductRepository.getContentList(id).catch(() => []),
          digitalProductRepository
            .getSalesInsights(id, productAllTimeRange(product.createdAt))
            .catch(() => ({totalSales: 0, totalRevenueByCurrency: {}})),
          mediaRepository.getCoverImages("product", id).catch(() => []),
        ]);

        const currencies: CurrencyResponse[] = product.currencies
          .filter((currency) => currency.currency != null)
          .map((currency, index) => ({
            id: index,
            userId: product.userId,
            service_id: product.id,
            service_type: 1,
            currency: currency.currency,
            amount: currency.amount,
            is_auto_converted: currency.isAutoConverted ?? false,
            is_base:
              currency.currency.toUpperCase() ===
                product.callPriceCurrency.toUpperCase() || currency.isBase === true,
            createdAt: new Date(product.createdAt),
            updatedAt: new Date(product.updatedAt),
          }));

        return {
          product: {
            id: product.id,
            userId: product.userId,
            title: product.name,
            slug: product.slug,
            description: product.description,
            coverImage: product.coverImage ?? coverImages[0]?.url ?? "",
            tiny_url: product.tinyUrl,
            currency: currencies[0],
            hideFromProfile: !product.isVisibleOnProfile(),
            is_deleted: false,
            request_phone_number: product.requestPhoneNumber,
            url_redirect: product.urlRedirect ?? undefined,
            statusId: product.statusId,
            reference: product.reference,
            createdAt: product.createdAt,
            updatedAt: product.updatedAt,
            enable_currency_conversion: product.enableCurrencyConversion,
            selected_currencies: product.selectedCurrencies ?? [],
          },
          totalSales: insights.totalSales,
          medias: contents.map((content) => ({
            id: content.id,
            userId: product.userId,
            productId: content.productId,
            file_name: content.fileName,
            file_type: content.fileType,
            // The contents list renders this string as-is, so it takes v2's
            // human-readable size rather than the raw byte count.
            file_size: content.humanReadableFileSize,
            media_url: content.mediaUrl,
            createdAt: content.createdAt,
            updatedAt: content.createdAt,
          })),
          currencies,
          // `units` has no v2 equivalent (and no consumer — the revenue tile
          // renders only currency + amount), so it stays at 0.
          revenue: Object.entries(insights.totalRevenueByCurrency).map(
            ([currency, amount]) => ({currency, amount, units: 0})
          ),
          enable_currency_conversion: product.enableCurrencyConversion,
          selected_currencies: product.selectedCurrencies ?? [],
        };
      },
      enabled: !!productId,
      // staleTime: Infinity,
      // refetchOnWindowFocus: true,
    }
  );
};

/**
 * One purchased digital product as returned by v2
 * `GET customer/digital-products`. Mirrors
 * `CustomerDigitalProductPurchaseResponseDto` exactly — `reference` is the
 * *transaction* reference, not the product reference.
 */
type CustomerDigitalProductV2Item = {
  reference: string;
  productId: number | string | null;
  productName: string | null;
  description: string | null;
  coverImage: string | null;
  urlRedirect: string | null;
  currency: string | null;
  amount: number | string | null;
  createdAt: string;
  coach?: {coachDisplayName?: string | null; profilePhoto?: string | null} | null;
};

/**
 * Adapts a v2 purchase row to the legacy library-row shape.
 *
 * Renames: `productName` -> `title`, `productId` -> `id`, `urlRedirect` ->
 * `url_redirect`. `coverImage` is passed through and also covers the card's
 * `cover_images[0].media_url` fallback, since the card reads `coverImage` first
 * and only falls through to `cover_images` (and then to the placeholder) when it
 * is empty.
 *
 * `description` and `coverImage` are nullable in v2 but the card calls
 * `.replace()` on the description and uses `coverImage` as a truthiness check,
 * so both are coerced to `""`.
 *
 * `url_redirect` stays *optional* rather than `""`: the drawer only renders the
 * "your purchase includes a link" block when it is truthy, and a null redirect
 * must leave the key absent so the block is skipped.
 */
export const toLegacyCustomerProduct = (
  item: CustomerDigitalProductV2Item
): GetCustomerProductsSingleProductResponse => ({
  id: Number(item.productId ?? 0),
  reference: item.reference,
  title: item.productName ?? "",
  description: item.description ?? "",
  coverImage: item.coverImage ?? "",
  currency: item.currency ?? "",
  amount: Number(item.amount ?? 0),
  createdAt: item.createdAt,
  ...(item.urlRedirect ? {url_redirect: item.urlRedirect} : {}),
});

/**
 * Unwraps the v2 envelope (`{status, message, data}`) plus the paginated body
 * (`{data, total, hasNextPage, hasPrevPage}`), tolerating a bare list.
 */
export const extractCustomerProductsPage = (
  raw: unknown
): {items: CustomerDigitalProductV2Item[]; total: number; hasNextPage: boolean} => {
  const empty = {
    items: [] as CustomerDigitalProductV2Item[],
    total: 0,
    hasNextPage: false,
  };
  if (!raw || typeof raw !== "object") {
    return empty;
  }
  const body = (raw as {data?: unknown}).data ?? raw;
  if (Array.isArray(body)) {
    return {
      items: body as CustomerDigitalProductV2Item[],
      total: body.length,
      hasNextPage: false,
    };
  }
  if (!body || typeof body !== "object") {
    return empty;
  }
  const node = body as {data?: unknown; total?: unknown; hasNextPage?: unknown};
  if (!Array.isArray(node.data)) {
    return empty;
  }
  const items = node.data as CustomerDigitalProductV2Item[];
  return {
    items,
    total: Number(node.total ?? items.length),
    hasNextPage: Boolean(node.hasNextPage),
  };
};

/**
 * Customer digital-product library list, on v2
 * `GET customer/digital-products?page=&perPage=`.
 *
 * The v2 payload is adapted back to the legacy `{data: {products}, meta}` shape
 * so the list, the card, and the store-tab visibility check in
 * `pages/dashboard/customer/products` keep working untouched. `perPage` (not
 * `per_page`) is required — v2's global validation pipe runs with
 * `whitelist: true` and silently drops unknown query params.
 */
export const useGetCustomerProducts = (page = 1, per_page = 5) => {
  const client = useClientV2();

  return useQuery<
    {data: TGetCustomerProductsApiResponse; meta: PaginationMeta},
    AxiosError<{status: boolean; message: string}>
  >({
    queryKey: [module_customer_query_key, page],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: String(page),
        perPage: String(per_page),
      });
      const raw = await client(`customer/digital-products?${params}`);
      const {items, total, hasNextPage} = extractCustomerProductsPage(raw);
      return {
        data: {products: items.map(toLegacyCustomerProduct)},
        meta: buildPaginationMeta(page, per_page, total, hasNextPage),
      };
    },
    refetchInterval: 300000,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  });
};

/**
 * Raw v2 body of `GET customer/digital-products/:reference`. Every nested node
 * is treated as optional so a partial payload degrades instead of throwing.
 */
type CustomerPurchaseDetailV2 = {
  reference?: string | null;
  currency?: string | null;
  amount?: number | string | null;
  createdAt?: string | null;
  product?: {
    id?: number | string | null;
    name?: string | null;
    slug?: string | null;
    tinyUrl?: string | null;
    description?: string | null;
    urlRedirect?: string | null;
    coverImage?: string | null;
  } | null;
  coach?: {
    username?: string | null;
    coachDisplayName?: string | null;
    shortBio?: string | null;
    profilePhoto?: string | null;
  } | null;
  assets?:
    | {
        id?: number | string | null;
        fileName?: string | null;
        fileType?: string | null;
        fileSize?: string | null;
        mediaUrl?: string | null;
      }[]
    | null;
  addOns?:
    | {
        id?: string | null;
        variantName?: string | null;
        options?:
          | {
              id?: string | null;
              optionName?: string | null;
              price?: number | string | null;
              currency?: string | null;
            }[]
          | null;
      }[]
    | null;
};

/**
 * Normalises the v2 purchase-detail payload into the shape the customer UI
 * renders.
 *
 * Nullable strings become `""` (they are interpolated into copy or passed to an
 * `<img src>`), except `profilePhoto` and `urlRedirect` which stay nullable
 * because they gate whether a block renders at all.
 *
 * `assets` and `addOns` are always arrays: `addOns` is legitimately empty for
 * v2-era purchases made before the backend started persisting the selected
 * options, so an empty list is a normal state, not an error.
 */
export const toCustomerPurchaseDetail = (
  raw: unknown
): TGetCustomerProductDetailApiResponse => {
  const envelope = (raw ?? {}) as {data?: unknown};
  const node = (envelope.data ?? raw ?? {}) as CustomerPurchaseDetailV2;
  const product = node.product ?? {};
  const coach = node.coach ?? {};

  return {
    reference: node.reference ?? "",
    currency: node.currency ?? "",
    amount: Number(node.amount ?? 0),
    createdAt: node.createdAt ?? "",
    product: {
      id: Number(product.id ?? 0),
      name: product.name ?? "",
      slug: product.slug ?? "",
      tinyUrl: product.tinyUrl ?? "",
      description: product.description ?? "",
      urlRedirect: product.urlRedirect ?? null,
      coverImage: product.coverImage ?? "",
    },
    coach: {
      username: coach.username ?? "",
      coachDisplayName: coach.coachDisplayName ?? "",
      shortBio: coach.shortBio ?? "",
      profilePhoto: coach.profilePhoto ?? null,
    },
    assets: (node.assets ?? []).map((asset) => ({
      id: Number(asset?.id ?? 0),
      fileName: asset?.fileName ?? "",
      fileType: asset?.fileType ?? "",
      fileSize: asset?.fileSize ?? "",
      mediaUrl: asset?.mediaUrl ?? "",
    })),
    addOns: (node.addOns ?? []).map((addOn) => ({
      id: addOn?.id ?? "",
      variantName: addOn?.variantName ?? "",
      options: (addOn?.options ?? []).map((option) => ({
        id: option?.id ?? "",
        optionName: option?.optionName ?? "",
        price: Number(option?.price ?? 0),
        currency: option?.currency ?? "",
      })),
    })),
  };
};

/**
 * Sums the prices of every option across the supplied add-ons.
 *
 * v2 has no `total`/`totalVariantPrice` field, so any add-on subtotal shown in
 * the UI has to be derived here. Note this is *not* a grand total: the
 * purchase's `amount` already includes these prices.
 */
export const sumCustomerPurchaseAddOns = (addOns: CustomerPurchaseAddOn[]): number =>
  addOns.reduce(
    (total, addOn) =>
      total + addOn.options.reduce((sum, option) => sum + (option.price || 0), 0),
    0
  );

/**
 * One purchased digital product, on v2
 * `GET customer/digital-products/:reference`.
 *
 * **Keyed by the purchase `reference`, not by product id** — v1's
 * `customer/products/:id` took the numeric product id, v2 scopes the lookup to
 * the calling customer's own purchase. Callers that only hold a product id must
 * resolve the reference first (see `useCustomerPurchaseReferenceByProductId`).
 */
export const useGetProductCustomer = (reference?: string) => {
  const client = useClientV2();

  return useQuery<
    TGetCustomerProductDetailApiResponse,
    AxiosError<{status: boolean; message: string}>
  >({
    queryKey: [module_customer_query_key, String(reference)],
    queryFn: () =>
      client(`customer/digital-products/${reference}`).then(toCustomerPurchaseDetail),
    enabled: !!reference,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
};

/** Page size used when scanning the library for a purchase reference. */
const REFERENCE_LOOKUP_PER_PAGE = 100;
/** Hard cap on pages scanned, so a huge library cannot spin forever. */
const REFERENCE_LOOKUP_MAX_PAGES = 10;

/**
 * Resolves the purchase `reference` for a numeric product id by scanning the
 * customer's own library (`GET customer/digital-products`).
 *
 * Only needed for the legacy `/customer/products/details/:id` URL, which was
 * minted against v1's product-id-keyed detail endpoint. v2 keys the detail by
 * reference and offers no product-id lookup, so the library — the one place
 * that maps a product id to the customer's purchase of it — is the bridge.
 *
 * Resolves to `null` (not an error) when the product is not in the library, so
 * the page can say "not found" rather than showing a fetch failure. If the same
 * product was bought more than once, the most recent purchase wins: the list is
 * ordered newest-first.
 */
export const useCustomerPurchaseReferenceByProductId = (productId?: number) => {
  const client = useClientV2();

  return useQuery<string | null, AxiosError<{status: boolean; message: string}>>({
    queryKey: [module_customer_query_key, "reference-for-product", productId],
    queryFn: async () => {
      for (let page = 1; page <= REFERENCE_LOOKUP_MAX_PAGES; page += 1) {
        const params = new URLSearchParams({
          page: String(page),
          perPage: String(REFERENCE_LOOKUP_PER_PAGE),
        });
        const raw = await client(`customer/digital-products?${params}`);
        const {items, hasNextPage} = extractCustomerProductsPage(raw);

        const match = items.find((item) => Number(item.productId) === productId);
        if (match) {
          return match.reference;
        }
        if (!hasNextPage) {
          break;
        }
      }
      return null;
    },
    enabled: !!productId,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
};
